import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(() => localStorage.getItem('discord_token'));
    const [initialized, setInitialized] = useState(false);

    // Handle token from URL on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        
        if (urlToken) {
            console.log('Token found in URL, saving...');
            localStorage.setItem('discord_token', urlToken);
            setToken(urlToken);
            // Clean URL without reload
            window.history.replaceState({}, document.title, '/dashboard');
        }
        setInitialized(true);
    }, []);

    // Fetch user when token changes
    useEffect(() => {
        const fetchUser = async () => {
            if (!initialized) return;
            
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                console.log('Fetching user with token...');
                const response = await axios.get(`${API}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                console.log('User fetched:', response.data.username);
                setUser(response.data);
            } catch (error) {
                console.error('Auth error:', error);
                localStorage.removeItem('discord_token');
                setToken(null);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [token, initialized]);

    const login = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/discord`);
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Login error:', error);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('discord_token');
        setToken(null);
        setUser(null);
        window.location.href = '/';
    }, []);

    const getAuthHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    return (
        <AuthContext.Provider value={{ user, loading, token, login, logout, getAuthHeader }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
